import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import React, { useContext, useMemo } from 'react';
import { isFinite } from 'lodash';
import { Alert } from '@material-ui/lab';

import { ModalContext } from '../context';
import { TabRow } from '../../../blocks';
import { InfoInput, OnwardPriceInput } from '../../InputFields';
import OrderDetailsPricing from './OrderDetailsTab/OrderDetailsPricing';

/**
 * Admin rate editor: shipper and carrier base overrides for each leg with the
 * resulting rates and margins, then the shipper and carrier pricing breakdowns.
 * Takes no props; reads the order and writes overrides through `ModalContext`.
 */
const RatesSection = () => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { order, adminHasError } = modalState;

    const [shipperOverrideBase, carrierOverrideBase] = useMemo(() => {
        return [
            order.admin_shipper_rate_override ?? order.listing?.final_accepted_shipper_rate,
            order.admin_carrier_rate_override ?? order.listing?.final_accepted_carrier_rate,
        ];
    }, [order]);

    const [mmMargin, fmMargin, totalMargin] = useMemo(() => {
        const shipperTotal = (order.shipper_rate || 0) + (order.middle_mile_shipper_rate || 0);
        const carrierTotal = (order.carrier_rate || 0) + (order.middle_mile_carrier_rate || 0);
        return [
            (100 * (order.middle_mile_shipper_rate - order.middle_mile_carrier_rate)) / order.middle_mile_shipper_rate,
            (100 * (order.shipper_rate - order.carrier_rate)) / order.shipper_rate,
            (100 * (shipperTotal - carrierTotal)) / shipperTotal,
        ];
    }, [order]);

    return (
        <>
            {adminHasError?.rate_change_note && (
                <TabRow>
                    <Alert severity="error">
                        An 'Invoicing' note is required when changing rate overrides. Please add a note with type
                        'Invoicing' in the Admin Notes section.
                    </Alert>
                </TabRow>
            )}

            {['shipperBreakdown', 'mmShipperBreakdown'].some(
                (attr) => !!order.price_breakdown?.[attr]?._meta?.original_quote
            ) ? (
                <TabRow>
                    <Alert severity="error">Rates set automatically via API</Alert>
                </TabRow>
            ) : null}

            <TabRow>
                <Grid item xs={3}>
                    <OnwardPriceInput
                        fullWidth
                        variant="outlined"
                        value={order.admin_middle_mile_shipper_rate_override}
                        label="MM Shipper Base Override"
                        onChange={(_, shipperRate) => {
                            const valid = isFinite(shipperRate);
                            callbacks.modifyOrder({
                                admin_middle_mile_shipper_rate_override: valid ? shipperRate : null,
                                order_revenue_adjustments: {
                                    ...(order.order_revenue_adjustments || {}),
                                    mmShipper: valid ? order.order_revenue_adjustments?.mmShipper || [] : null,
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput
                        value={
                            isFinite(order.middle_mile_shipper_rate) ? order.middle_mile_shipper_rate.toFixed(2) : 'N/A'
                        }
                        label="MM Shipper Rate"
                    />
                </Grid>
                <Grid item xs={3}>
                    <OnwardPriceInput
                        fullWidth
                        variant="outlined"
                        value={order.admin_middle_mile_carrier_rate_override}
                        label="MM Carrier Base Override"
                        onChange={(_, carrierRate) => {
                            const valid = isFinite(carrierRate);
                            callbacks.modifyOrder({
                                admin_middle_mile_carrier_rate_override: valid ? carrierRate : null,
                                order_revenue_adjustments: {
                                    ...(order.order_revenue_adjustments || {}),
                                    mmCarrier: valid ? order.order_revenue_adjustments?.mmCarrier || [] : null,
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput
                        value={
                            isFinite(order.middle_mile_carrier_rate) ? order.middle_mile_carrier_rate.toFixed(2) : 'N/A'
                        }
                        label="MM Carrier Rate"
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput value={isFinite(mmMargin) ? `${mmMargin.toFixed(2)} %` : 'N/A'} label="MM Margin" />
                </Grid>
            </TabRow>
            <TabRow>
                <Grid item xs={3}>
                    <OnwardPriceInput
                        fullWidth
                        variant="outlined"
                        value={shipperOverrideBase}
                        label="Shipper Base Override"
                        onChange={(_, shipperRate) => {
                            const valid = isFinite(shipperRate);
                            callbacks.modifyOrder({
                                admin_shipper_rate_override: valid ? shipperRate : null,
                                order_revenue_adjustments: {
                                    ...(order.order_revenue_adjustments || {}),
                                    shipper: valid ? order.order_revenue_adjustments?.shipper || [] : null,
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput
                        value={isFinite(order.shipper_rate) ? order.shipper_rate.toFixed(2) : 'N/A'}
                        label="Shipper Rate"
                    />
                </Grid>
                <Grid item xs={3}>
                    <OnwardPriceInput
                        fullWidth
                        variant="outlined"
                        value={carrierOverrideBase}
                        label="Carrier Base Override"
                        onChange={(_, carrierRate) => {
                            const valid = isFinite(carrierRate);
                            callbacks.modifyOrder({
                                admin_carrier_rate_override: valid ? carrierRate : null,
                                order_revenue_adjustments: {
                                    ...(order.order_revenue_adjustments || {}),
                                    carrier: valid ? order.order_revenue_adjustments?.carrier || [] : null,
                                },
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput
                        value={isFinite(order.carrier_rate) ? order.carrier_rate.toFixed(2) : 'N/A'}
                        label="Carrier Rate"
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput value={isFinite(fmMargin) ? `${fmMargin.toFixed(2)} %` : 'N/A'} label="Margin" />
                </Grid>
            </TabRow>
            <TabRow>
                <Grid item xs={5}>
                    <InfoInput
                        value={((order.shipper_rate || 0) + (order.middle_mile_shipper_rate || 0)).toFixed(2)}
                        label="Total Shipper Rate"
                    />
                </Grid>
                <Grid item xs={5}>
                    <InfoInput
                        value={((order.carrier_rate || 0) + (order.middle_mile_carrier_rate || 0)).toFixed(2)}
                        label="Total Carrier Rate"
                    />
                </Grid>
                <Grid item xs={2}>
                    <InfoInput
                        value={isFinite(totalMargin) ? `${totalMargin.toFixed(2)} %` : 'N/A'}
                        label="Total Margin"
                    />
                </Grid>
            </TabRow>

            <hr
                css={css`
                    margin: 0;
                    border: none;
                    border-top: 2px solid gray;
                `}
            />

            <OrderDetailsPricing perspective="shipper" />
            <hr
                css={css`
                    margin: 0;
                    border: none;
                    border-top: 2px solid gray;
                `}
            />
            <OrderDetailsPricing perspective="carrier" />
        </>
    );
};

export default RatesSection;
